import { AddOutlined as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    IconButton,
    LinearProgress,
    Pagination,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { BarraRelatorio } from 'components';
import { usePopup } from 'contexts';
import { useDebounce } from 'hooks';
import { Base } from 'layout';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UsersService } from 'services';
import { Constants } from 'utils';

export const Users = () => {
    const navigate = useNavigate();
    const { debounce } = useDebounce();
    const [searchParams, setSearchParams] = useSearchParams();
    const [createPopup, closePopup] = usePopup();
    const [loading, setLoading] = useState(true);
    const [records, setRecords] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [error, setError] = useState('');

    const handlePopupConfirmStatus = (record) => {
        const nextStatus = !record.is_active;
        createPopup({
            title: nextStatus ? 'Ativar?' : 'Desativar?',
            content: `Você realmente deseja ${nextStatus ? 'ativar' : 'desativar'} o usuário?`,
            onClose: closePopup,
            actions: (
                <Fragment>
                    <Button
                        onClick={closePopup}
                        autoFocus>
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => {
                            handleToggleStatus(record, nextStatus);
                            closePopup();
                        }}>
                        {nextStatus ? 'Ativar' : 'Desativar'}
                    </Button>
                </Fragment>
            )
        });
    };

    const handleToggleStatus = (record, nextStatus) => {
        UsersService.updateById(record.id, { ...record, id: record.id, is_active: nextStatus }).then((result) => {
            if (result instanceof Error) {
                console.log(result.message);
            } else {
                setRecords((records) =>
                    records.map((item) => (item.id === record.id ? { ...item, is_active: nextStatus } : item))
                );
                handlePopupOkStatus(nextStatus);
            }
        });
    };

    const handlePopupOkStatus = (isActive) => {
        createPopup({
            title: isActive ? 'Ativado' : 'Desativado',
            content: `Usuário ${isActive ? 'ativado' : 'desativado'} com sucesso!`,
            actions: (
                <Fragment>
                    <Button
                        onClick={closePopup}
                        autoFocus>
                        Fechar
                    </Button>
                </Fragment>
            )
        });
    };

    const busca = useMemo(() => {
        return searchParams.get('busca') || '';
    }, [searchParams]);

    const pagina = useMemo(() => {
        return Number(searchParams.get('pagina') || '1');
    }, [searchParams]);

    useEffect(() => {
        setLoading(true);
        setError('');
        debounce(() => {
            UsersService.getAll(pagina, busca).then((result) => {
                setLoading(false);
                if (result instanceof Error) {
                    setError(result.message || Constants.MSG_ERRO_AUTENTICACAO);
                    setRecords([]);
                    setTotalRecords(0);
                } else {
                    setTotalRecords(result.total);
                    setRecords(result.users || []);
                }
            });
        });
    }, [busca, pagina]);

    return (
        <Base
            titulo='Lista de Usuários'
            subTitulo='Visualize aqui a lista de usuários do sistema, com informações consolidadas para acompanhamento e gestão administrativa.'
            barra={
                <BarraRelatorio
                    showSearch
                    textSearch={busca}
                    onChangeSearch={(value) => setSearchParams({ pagina: '1', busca: value }, { replace: true })}
                    labelSearch='Buscar...'
                    textAux='Novo'
                    iconAux={<AddIcon />}
                    onClickAux={() => {
                        navigate('/users/detalhe/new');
                    }}
                />
            }>
            <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                component={Paper}
                elevation={0}
                variant='outlined'
                marginY={2}
                marginRight={2}
                padding={2}>
                {loading && <LinearProgress variant='indeterminate' />}
                <TableContainer
                    component={Paper}
                    elevation={0}
                    variant='outlined'>
                    <Table size='medium'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Perfil</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>CPF</TableCell>
                                <TableCell>Desativar</TableCell>
                                <TableCell>Editar</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {error && (
                                <TableRow>
                                    <TableCell
                                        colSpan={6}
                                        align='center'>
                                        <Alert severity='error'>{error}</Alert>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!error && !loading && totalRecords === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={6}
                                        align='center'>
                                        <Alert severity='info'>{Constants.MSG_LISTAGEM_USUARIOS}</Alert>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!error &&
                                records.map((record) => (
                                    <TableRow key={record.id}>
                                        <TableCell>{record.name}</TableCell>
                                        <TableCell>{record.is_admin ? 'Administrador' : 'Usuário'}</TableCell>
                                        <TableCell>{record.email}</TableCell>
                                        <TableCell>{record.cpf}</TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={record.is_active}
                                                onChange={() => handlePopupConfirmStatus(record)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                color='primary'
                                                onClick={() => navigate(`/users/detalhe/${record.id}`)}>
                                                <EditIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {totalRecords > 0 && totalRecords > Constants.USUARIOS && (
                    <Pagination
                        count={Math.ceil(totalRecords / Constants.USUARIOS)}
                        onChange={(e, page) => setSearchParams({ busca, pagina: page.toString() }, { replace: true })}
                        page={pagina}
                        sx={{ marginTop: 2 }}
                    />
                )}
            </Box>
        </Base>
    );
};
